var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, TextField, Typography, InputAdornment, Chip, } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Trash, PencilSimple, Plus, MagnifyingGlass, CaretLeft, CaretRight, Warning } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomModal from "../modal/CustomModal";
import { WeeklyTargetFormModal } from "./WeeklyTargetsFormModal";
import DashboardKpiApi from "../../../infrastructure/api/pages/dashboard-kpi-api";
import { getCurrentISOWeek, formatDisplayDate } from "../../../application/common/utils/kpi-helpers";
import { DATA_GRID_SX, formatEuro } from "../../components/dashboard/kpi-shared-styles";
export const WeeklyTargetsModal = ({ open, onClose, canCreate, canManage, canDelete }) => {
    var _a;
    const { t: translate } = useTranslation();
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [targetToDelete, setTargetToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const currentYear = new Date().getFullYear();
    const currentWeek = useMemo(() => getCurrentISOWeek(), []);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    useEffect(() => {
        if (open)
            fetchTargets(selectedYear);
    }, [open, selectedYear]);
    const fetchTargets = (year) => __awaiter(void 0, void 0, void 0, function* () {
        setLoadingData(true);
        try {
            const res = yield DashboardKpiApi.getWeeklyTargets(year);
            const data = res.data;
            setRows(data.map((t) => ({
                id: t.id,
                year: t.year,
                weekNumber: t.weekNumber,
                startDate: formatDisplayDate(new Date(t.startDate)),
                objectsSent: t.objectsSentTarget,
                objectsWon: t.objectsWonTarget,
                revenueWon: t.revenueWonTarget,
            })));
        }
        catch (_a) {
            toast.error(translate("FailedToLoadTargets"));
        }
        finally {
            setLoadingData(false);
        }
    });
    const existingWeeks = useMemo(() => rows.map((r) => r.weekNumber), [rows]);
    const handleDeleteClick = (id) => {
        setTargetToDelete(id);
        setDeleteConfirmOpen(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!targetToDelete)
            return;
        setDeleting(true);
        try {
            yield DashboardKpiApi.deleteWeeklyTarget(targetToDelete);
            setRows((prev) => prev.filter((r) => r.id !== targetToDelete));
            toast.success(translate("TargetDeleted"));
        }
        catch (_a) {
            toast.error(translate("FailedToDeleteTarget"));
        }
        finally {
            setDeleting(false);
            setDeleteConfirmOpen(false);
            setTargetToDelete(null);
        }
    });
    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setTargetToDelete(null);
    };
    const handleEditRow = (row) => {
        setEditingRow(row);
        setFormOpen(true);
    };
    const handleAddTarget = () => {
        setEditingRow({
            id: "",
            year: selectedYear,
            weekNumber: 0,
            startDate: "",
            objectsSent: 0,
            objectsWon: 0,
            revenueWon: 0,
        });
        setFormOpen(true);
    };
    const handleSaveForm = (updatedRow) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const startDateParts = updatedRow.startDate.split("-");
            const isoDate = startDateParts.length === 3 && startDateParts[2].length === 4
                ? `${startDateParts[2]}-${startDateParts[1]}-${startDateParts[0]}`
                : updatedRow.startDate;
            const payload = {
                year: updatedRow.year,
                weekNumber: updatedRow.weekNumber,
                startDate: isoDate,
                objectsSentTarget: updatedRow.objectsSent,
                objectsWonTarget: updatedRow.objectsWon,
                revenueWonTarget: updatedRow.revenueWon,
            };
            if (updatedRow.id) {
                yield DashboardKpiApi.updateWeeklyTarget(updatedRow.id, payload);
                toast.success(translate("TargetUpdated"));
            }
            else {
                yield DashboardKpiApi.createWeeklyTarget(payload);
                toast.success(translate("TargetCreated"));
            }
            if (updatedRow.year === selectedYear) {
                yield fetchTargets(selectedYear);
            }
            else {
                setSelectedYear(updatedRow.year);
            }
        }
        catch (_a) {
            toast.error(translate("FailedToSaveTarget"));
        }
        finally {
            setFormOpen(false);
            setEditingRow(null);
        }
    });
    const isCurrentYear = selectedYear === currentYear;
    const filteredRows = useMemo(() => {
        if (!search)
            return rows;
        const term = search.toLowerCase();
        return rows.filter((r) => `${r.weekNumber} ${r.startDate} ${r.objectsSent} ${r.objectsWon} ${r.revenueWon}`.toLowerCase().includes(term));
    }, [rows, search]);
    const showActions = canManage || canDelete;
    const columns = useMemo(() => {
        const cols = [
            {
                field: "weekNumber",
                headerName: `${translate("Week")} #`,
                minWidth: 110,
                flex: 0.6,
                sortable: true,
                disableColumnMenu: true,
                renderCell: (params) => (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [params.value, isCurrentYear && params.value === currentWeek && (_jsx(Chip, { label: translate("current"), size: "small", sx: { height: 20, fontSize: "0.7rem", backgroundColor: "#009DDC", color: "#fff" } }))] })),
            },
            { field: "startDate", headerName: translate("Start Date"), minWidth: 120, flex: 0.8, sortable: true, disableColumnMenu: true },
            { field: "objectsSent", headerName: translate("Objects Sent"), minWidth: 120, flex: 0.7, sortable: true, disableColumnMenu: true },
            { field: "objectsWon", headerName: translate("Objects Won"), minWidth: 120, flex: 0.7, sortable: true, disableColumnMenu: true },
            {
                field: "revenueWon",
                headerName: translate("Revenue Won"),
                minWidth: 140,
                flex: 0.8,
                sortable: true,
                disableColumnMenu: true,
                valueFormatter: (params) => formatEuro(params),
            },
        ];
        if (showActions) {
            cols.push({
                field: "actions",
                headerName: translate("Actions"),
                minWidth: 100,
                flex: 0.5,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: "right",
                align: "right",
                renderCell: (params) => (_jsxs(Box, { children: [canManage && (_jsx(IconButton, { size: "small", onClick: () => handleEditRow(params.row), children: _jsx(PencilSimple, { size: 18 }) })), canDelete && (_jsx(IconButton, { size: "small", color: "error", onClick: () => handleDeleteClick(params.row.id), children: _jsx(Trash, { size: 18 }) }))] })),
            });
        }
        return cols;
    }, [translate, isCurrentYear, currentWeek, showActions, canManage, canDelete]);
    return (_jsxs(_Fragment, { children: [_jsx(CustomModal, { open: open, onClose: onClose, size: "lg", saving: false, handleSaveButtonClick: onClose, heading: translate("Set Weekly Targets"), saveButtonText: translate("Close"), disabled: false, showSaveButton: false, disableDialogContentScroll: true, children: _jsxs(Box, { sx: {
                        p: 3,
                        height: "min(820px, calc(100vh - 180px))",
                        maxHeight: "calc(100vh - 180px)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }, children: [_jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexDirection: { xs: "column", sm: "row" }, gap: 2, flexShrink: 0 }, children: [_jsx(TextField, { placeholder: translate("Search here..."), value: search, onChange: (e) => setSearch(e.target.value), slotProps: {
                                        input: {
                                            endAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(MagnifyingGlass, { size: 18 }) })),
                                        }
                                    } }), _jsxs(Box, { sx: { display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }, children: [_jsx(IconButton, { size: "small", onClick: () => setSelectedYear((y) => y - 1), sx: { border: "1px solid #dee2e6", borderRadius: 1 }, children: _jsx(CaretLeft, { size: 18, weight: "bold" }) }), _jsx(Typography, { variant: "h6", sx: {
                                                fontWeight: 600,
                                                minWidth: 50,
                                                textAlign: "center"
                                            }, children: selectedYear }), _jsx(IconButton, { size: "small", onClick: () => setSelectedYear((y) => y + 1), sx: { border: "1px solid #dee2e6", borderRadius: 1 }, children: _jsx(CaretRight, { size: 18, weight: "bold" }) })] }), canCreate && (_jsx(Button, { variant: "outlined", color: "secondary", startIcon: _jsx(Plus, { size: 18, color: "#009DDC" }), onClick: handleAddTarget, sx: {
                                        whiteSpace: "nowrap",
                                        border: "1px solid #009DDC",
                                        color: "#009DDC",
                                        "&:hover": { backgroundColor: "transparent", color: "#009DDC", border: "1px solid #009DDC" },
                                    }, children: translate("Add Week Target") }))] }), _jsx(Box, { sx: { width: "100%", flex: 1, minHeight: 0 }, children: _jsx(DataGrid, { loading: loadingData, rows: filteredRows, columns: columns, getRowId: (row) => row.id || `new-${row.weekNumber}`, pagination: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, pageSizeOptions: [5, 10, 20, 50], disableRowSelectionOnClick: true, rowHeight: 52, localeText: {
                                    noRowsLabel: translate("No weekly targets yet"),
                                    noResultsOverlayLabel: translate("NoResultsFound"),
                                }, sx: Object.assign(Object.assign({}, DATA_GRID_SX), { height: "97%", "& .MuiDataGrid-main": { overflow: "hidden" } }), sortingOrder: ["asc", "desc"] }) })] }) }), _jsxs(Dialog, { open: deleteConfirmOpen, onClose: handleDeleteCancel, maxWidth: "xs", fullWidth: true, slotProps: {
                    paper: { sx: { borderRadius: 4, overflow: "hidden" } }
                }, children: [_jsx(Box, { sx: { height: 4, backgroundColor: "#e03131", flexShrink: 0 } }), _jsxs(DialogContent, { sx: { textAlign: "center", pt: 4.5, pb: 2, px: 4 }, children: [_jsx(Box, { sx: {
                                    width: 68,
                                    height: 68,
                                    borderRadius: "50%",
                                    backgroundColor: "#fff5f5",
                                    border: "2px solid #ffc9c9",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto 20px",
                                }, children: _jsx(Warning, { size: 34, color: "#e03131", weight: "fill" }) }), _jsx(Typography, { sx: {
                                    fontWeight: 700,
                                    fontSize: "1.125rem",
                                    color: "#130922",
                                    mb: 1.25
                                }, children: translate("ConfirmDelete") }), _jsxs(Typography, { sx: {
                                    color: "#6c757d",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.65
                                }, children: [translate("DeleteTargetConfirmation").split(".")[0] + ". ", _jsx(Box, { component: "span", sx: { color: "#495057", fontWeight: 500 }, children: (_a = translate("DeleteTargetConfirmation").split(".")[1]) === null || _a === void 0 ? void 0 : _a.trim() })] })] }), _jsxs(DialogActions, { sx: { px: 4, pb: 4, pt: 2, gap: 1.5 }, children: [_jsx(Button, { onClick: handleDeleteCancel, variant: "outlined", size: "medium", fullWidth: true, disabled: deleting, sx: {
                                    borderColor: "#dee2e6",
                                    color: "#495057",
                                    textTransform: "none",
                                    borderRadius: 2.5,
                                    fontWeight: 500,
                                    "&:hover": { borderColor: "#adb5bd", backgroundColor: "#f8f9fa" },
                                }, children: translate("Cancel") }), _jsx(Button, { onClick: handleDeleteConfirm, variant: "contained", size: "medium", fullWidth: true, disabled: deleting, sx: {
                                    backgroundColor: "#e03131",
                                    "&:hover": { backgroundColor: "#c92a2a" },
                                    textTransform: "none",
                                    borderRadius: 2.5,
                                    fontWeight: 600,
                                    boxShadow: "0 2px 10px rgba(224,49,49,0.28)",
                                }, children: deleting ? _jsx(CircularProgress, { size: 20, color: "inherit" }) : translate("Delete") })] })] }), editingRow && (_jsx(WeeklyTargetFormModal, { open: formOpen, onClose: () => { setFormOpen(false); setEditingRow(null); }, initialValue: editingRow, onSave: handleSaveForm, existingWeeks: existingWeeks }))] }));
};
export default WeeklyTargetsModal;
